import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Wand2, Check, X, CircleHelp, HelpCircle, RotateCcw, Loader2, Sparkles, Share2, Trophy } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { CinematicVideo } from "@/components/CinematicVideo";

const DEVINEUR_VIDEO = "https://customer-assets-7cd3h4nn.emergentagent.net/job_epiphany-mentor/artifacts/z7q0o5dq_deevid_video_32650950.mp4";

const MAX_QUESTIONS = 25;

const T = {
  fr: { eyebrow: "Le Devineur", title: "Pense à quelque chose. Je vais le deviner.", intro: "Un personnage, un animal, un objet, un lieu ou un concept — garde-le en tête. Je te pose des questions, tu réponds simplement.", start: "C'est parti", yes: "Oui", no: "Non", maybe: "Peut-être", unknown: "Je ne sais pas", thinking: "Je réfléchis…", questionLabel: "Question", guessLead: "Je crois savoir…", guessYes: "Oui, bravo !", guessNo: "Non, continue", wonTitle: "Deviné !", wonSub: (n) => `Trouvé en ${n} question${n > 1 ? "s" : ""}.`, giveTitle: "Tu m'as eu !", giveSub: "Je n'ai pas trouvé cette fois. Bien joué — ta pensée était insaisissable.", replay: "Rejouer", shareBtn: "Partager mon score", recordBadge: "Nouveau record !", leaderboard: "Tes meilleures parties", shareFooter: "Éveille ta curiosité sur", shareDownloaded: "Image enregistrée !", shareText: (a, n) => `Le Devineur de COCOON a deviné « ${a} » en ${n} question${n > 1 ? "s" : ""} ! Sauras-tu le battre ? 👉` , globalTitle: "Classement mondial", pseudoPlaceholder: "Ton pseudo", publish: "Publier mon score", publishedRank: (r) => `Publié ! Tu es #${r} au classement mondial.`, globalEmpty: "Sois le premier à entrer dans le classement !", questionsShort: "Q", weekTab: "Cette semaine", allTab: "Tout temps" },
  en: { eyebrow: "The Guesser", title: "Think of something. I'll guess it.", intro: "A character, animal, object, place or concept — keep it in mind. I ask questions, you just answer.", start: "Let's go", yes: "Yes", no: "No", maybe: "Maybe", unknown: "I don't know", thinking: "Thinking…", questionLabel: "Question", guessLead: "I think I know…", guessYes: "Yes, well done!", guessNo: "No, keep going", wonTitle: "Guessed it!", wonSub: (n) => `Found in ${n} question${n > 1 ? "s" : ""}.`, giveTitle: "You got me!", giveSub: "I couldn't find it this time. Well played — your thought was elusive.", replay: "Play again", shareBtn: "Share my score", recordBadge: "New record!", leaderboard: "Your best games", shareFooter: "Awaken your curiosity at", shareDownloaded: "Image saved!", shareText: (a, n) => `COCOON's Guesser found "${a}" in ${n} question${n > 1 ? "s" : ""}! Can you beat it? 👉` , globalTitle: "Global leaderboard", pseudoPlaceholder: "Your nickname", publish: "Publish my score", publishedRank: (r) => `Published! You're #${r} worldwide.`, globalEmpty: "Be the first on the leaderboard!", questionsShort: "Q", weekTab: "This week", allTab: "All time" },
  es: { eyebrow: "El Adivino", title: "Piensa en algo. Lo adivinaré.", intro: "Un personaje, animal, objeto, lugar o concepto — tenlo en mente. Yo pregunto, tú respondes.", start: "Empezar", yes: "Sí", no: "No", maybe: "Quizás", unknown: "No lo sé", thinking: "Pensando…", questionLabel: "Pregunta", guessLead: "Creo que lo sé…", guessYes: "¡Sí, bravo!", guessNo: "No, sigue", wonTitle: "¡Adivinado!", wonSub: (n) => `Encontrado en ${n} pregunta${n > 1 ? "s" : ""}.`, giveTitle: "¡Me ganaste!", giveSub: "No lo encontré esta vez. Bien jugado — tu idea fue escurridiza.", replay: "Jugar de nuevo", shareBtn: "Compartir mi puntuación", recordBadge: "¡Nuevo récord!", leaderboard: "Tus mejores partidas", shareFooter: "Despierta tu curiosidad en", shareDownloaded: "¡Imagen guardada!", shareText: (a, n) => `¡El Adivino de COCOON adivinó «${a}» en ${n} pregunta${n > 1 ? "s" : ""}! ¿Podrás superarlo? 👉` , globalTitle: "Clasificación mundial", pseudoPlaceholder: "Tu apodo", publish: "Publicar mi puntuación", publishedRank: (r) => `¡Publicado! Eres #${r} en el mundo.`, globalEmpty: "¡Sé el primero en la clasificación!", questionsShort: "P", weekTab: "Esta semana", allTab: "Histórico" },
  nl: { eyebrow: "De Rader", title: "Denk aan iets. Ik raad het.", intro: "Een personage, dier, voorwerp, plaats of concept — hou het in gedachten. Ik stel vragen, jij antwoordt.", start: "Start", yes: "Ja", no: "Nee", maybe: "Misschien", unknown: "Weet ik niet", thinking: "Aan het denken…", questionLabel: "Vraag", guessLead: "Ik denk dat ik het weet…", guessYes: "Ja, goed gedaan!", guessNo: "Nee, ga door", wonTitle: "Geraden!", wonSub: (n) => `Gevonden in ${n} vra${n > 1 ? "gen" : "ag"}.`, giveTitle: "Je hebt me!", giveSub: "Ik vond het deze keer niet. Goed gespeeld — je gedachte was ongrijpbaar.", replay: "Opnieuw spelen", shareBtn: "Deel mijn score", recordBadge: "Nieuw record!", leaderboard: "Je beste spellen", shareFooter: "Wek je nieuwsgierigheid op", shareDownloaded: "Afbeelding opgeslagen!", shareText: (a, n) => `De Rader van COCOON raadde "${a}" in ${n} vra${n > 1 ? "gen" : "ag"}! Kun jij het verslaan? 👉` , globalTitle: "Wereldranglijst", pseudoPlaceholder: "Je bijnaam", publish: "Publiceer mijn score", publishedRank: (r) => `Gepubliceerd! Je bent #${r} wereldwijd.`, globalEmpty: "Wees de eerste op de ranglijst!", questionsShort: "V", weekTab: "Deze week", allTab: "Aller tijden" },
};

export const Devineur = () => {
  const { i18n } = useTranslation();
  const lang = (i18n.language || "fr").slice(0, 2);
  const c = T[lang] || T.fr;

  const [phase, setPhase] = useState("idle"); // idle | asking | thinking | guessing | won | give
  const [current, setCurrent] = useState(null); // {action, content, count}
  const [transcript, setTranscript] = useState([]);
  const [wrong, setWrong] = useState([]);
  const [wonAnswer, setWonAnswer] = useState("");
  const [wonQuestions, setWonQuestions] = useState(0);
  const [isRecord, setIsRecord] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [stats, setStats] = useState(() => {
    try { return JSON.parse(localStorage.getItem("cocoon_devineur_stats")) || { best: null, games: [] }; }
    catch { return { best: null, games: [] }; }
  });
  const [name, setName] = useState(() => { try { return localStorage.getItem("cocoon_devineur_name") || ""; } catch { return ""; } });
  const [submitted, setSubmitted] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [myRank, setMyRank] = useState(null);
  const [board, setBoard] = useState([]);
  const [period, setPeriod] = useState("week");

  const loadBoard = (p = period) => api.get(`/devineur/leaderboard?limit=10&period=${p}`).then(({ data }) => setBoard(data.items || [])).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadBoard("week"); }, []);
  const switchPeriod = (p) => { setPeriod(p); loadBoard(p); };

  const publishScore = async () => {
    const nm = (name || "").trim().slice(0, 24);
    if (!nm || !wonAnswer || publishing) return;
    setPublishing(true);
    try {
      try { localStorage.setItem("cocoon_devineur_name", nm); } catch { /* noop */ }
      const { data } = await api.post("/devineur/score", { name: nm, answer: wonAnswer, questions: wonQuestions });
      setMyRank(data.rank);
      setSubmitted(true);
      loadBoard();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Oups, réessaie.");
    } finally { setPublishing(false); }
  };

  const callStep = async (nextTranscript, nextWrong) => {
    setPhase("thinking");
    try {
      const { data } = await api.post("/devineur/step", { transcript: nextTranscript, wrong_guesses: nextWrong, lang });
      if (data.action === "question" && nextTranscript.length >= MAX_QUESTIONS) {
        setPhase("give");
        return;
      }
      setCurrent(data);
      setPhase(data.action === "guess" ? "guessing" : "asking");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Oups, réessaie.");
      setPhase(nextTranscript.length ? "asking" : "idle");
    }
  };

  const start = () => {
    setTranscript([]);
    setWrong([]);
    setCurrent(null);
    setSubmitted(false);
    setMyRank(null);
    callStep([], []);
  };

  const answer = (label) => {
    if (phase !== "asking" || !current) return;
    const next = [...transcript, { q: current.content, a: label }];
    setTranscript(next);
    callStep(next, wrong);
  };

  const guessYes = () => {
    const answer = current?.content || "";
    const q = transcript.length;
    const record = stats.best == null || q < stats.best;
    const games = [{ answer, questions: q, at: Date.now() }, ...(stats.games || [])].slice(0, 20);
    const next = { best: record ? q : stats.best, games };
    setStats(next);
    try { localStorage.setItem("cocoon_devineur_stats", JSON.stringify(next)); } catch { /* noop */ }
    setWonAnswer(answer);
    setWonQuestions(q);
    setIsRecord(record);
    setPhase("won");
  };
  const guessNo = () => {
    if (!current) return;
    const nextWrong = [...wrong, current.content];
    setWrong(nextWrong);
    callStep(transcript, nextWrong);
  };

  const loadImg = (src) => new Promise((res) => { const im = new Image(); im.onload = () => res(im); im.onerror = () => res(null); im.src = src; });

  const buildCard = async () => {
    const W = 1080, H = 1080;
    const cv = document.createElement("canvas"); cv.width = W; cv.height = H;
    const ctx = cv.getContext("2d");
    ctx.fillStyle = "#0a0806"; ctx.fillRect(0, 0, W, H);
    const glow = ctx.createRadialGradient(W / 2, 320, 40, W / 2, 320, 780);
    glow.addColorStop(0, "rgba(240,155,45,0.28)"); glow.addColorStop(1, "rgba(0,0,0,0)");
    ctx.fillStyle = glow; ctx.fillRect(0, 0, W, H);
    ctx.strokeStyle = "rgba(240,170,90,0.35)"; ctx.lineWidth = 3;
    if (ctx.roundRect) { ctx.beginPath(); ctx.roundRect(28, 28, W - 56, H - 56, 40); ctx.stroke(); }
    const brand = await loadImg("/cocoon-logo-glow.png");
    if (brand && brand.height) {
      const bh = 130, bw = bh * (brand.width / brand.height);
      ctx.save(); ctx.shadowColor = "rgba(240,155,45,0.45)"; ctx.shadowBlur = 34;
      ctx.drawImage(brand, (W - bw) / 2, 96, bw, bh); ctx.restore();
    }
    ctx.textAlign = "center";
    try { ctx.letterSpacing = "8px"; } catch { /* noop */ }
    ctx.fillStyle = "rgba(240,175,95,0.92)"; ctx.font = "600 30px Georgia, serif";
    ctx.fillText("LE DEVINEUR · COCOON", W / 2, 310);
    try { ctx.letterSpacing = "0px"; } catch { /* noop */ }
    ctx.fillStyle = "#f4ede2"; ctx.font = "italic 38px Georgia, serif";
    ctx.fillText(c.guessLead, W / 2, 424);
    ctx.fillStyle = "rgba(245,180,95,1)"; ctx.font = "700 76px Georgia, serif";
    const words = (wonAnswer || "").split(" "); let line = ""; const lines = [];
    for (const w of words) { const test = line ? `${line} ${w}` : w; if (ctx.measureText(test).width > W - 200 && line) { lines.push(line); line = w; } else line = test; }
    if (line) lines.push(line);
    let y = 540; for (const l of lines.slice(0, 3)) { ctx.fillText(l, W / 2, y); y += 88; }
    ctx.fillStyle = "#f0c88a"; ctx.font = "600 46px Georgia, serif";
    ctx.fillText(c.wonSub(wonQuestions), W / 2, y + 46);
    ctx.fillStyle = "rgba(255,255,255,0.55)"; ctx.font = "500 30px Georgia, serif";
    ctx.fillText(c.shareFooter, W / 2, H - 150);
    ctx.fillStyle = "rgba(245,180,95,1)"; ctx.font = "700 42px Georgia, serif";
    ctx.fillText("cocoonmind.com", W / 2, H - 88);
    return new Promise((res) => cv.toBlob((b) => res(b), "image/png"));
  };

  const shareCard = async () => {
    if (sharing) return;
    setSharing(true);
    try {
      const blob = await buildCard();
      const file = new File([blob], "devineur-cocoon.png", { type: "image/png" });
      const shareText = c.shareText(wonAnswer, wonQuestions);
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        try { await navigator.share({ files: [file], title: "COCOON — Le Devineur", text: shareText }); setSharing(false); return; }
        catch { /* annulé → fallback */ }
      }
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = "devineur-cocoon.png"; a.click();
      URL.revokeObjectURL(url);
      toast.success(c.shareDownloaded);
    } catch { toast.error("Oups, réessaie."); }
    finally { setSharing(false); }
  };

  const answered = transcript.length;

  return (
    <section className="relative px-6 my-20" data-testid="devineur">
      <div className="max-w-4xl mx-auto">
        <div className="relative rounded-[2rem] overflow-hidden border border-primary/20 frame-pulse glass">
          <div aria-hidden className="absolute inset-0 pointer-events-none">
            <div className="absolute left-1/2 top-16 -translate-x-1/2 w-[560px] h-[560px] rounded-full bg-primary/10 blur-[120px]" />
          </div>

          <div className="relative z-10 px-6 sm:px-12 py-14 text-center">
            <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-primary/15 border border-primary/40 text-primary text-[11px] uppercase tracking-[0.28em] mb-6" data-testid="devineur-badge">
              <Wand2 size={13} /> {c.eyebrow}
            </span>
            <h2 className="font-display text-gradient-orange text-4xl sm:text-6xl leading-[1.12] pb-[0.08em] mb-4" data-testid="devineur-title">
              {c.title}
            </h2>
            <p className="text-foreground/65 text-base sm:text-lg max-w-xl mx-auto leading-relaxed mb-9">{c.intro}</p>

            {phase === "idle" && (
              <div className="max-w-xl mx-auto mb-9" data-testid="devineur-cinematic">
                <CinematicVideo fade noSound silver src={DEVINEUR_VIDEO} testId="devineur-cinematic-video" caption={c.title} />
              </div>
            )}

            <AnimatePresence mode="wait">
              {phase === "idle" && (
                <motion.div key="idle" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
                  <button onClick={start} className="btn-frost-orange inline-flex items-center gap-3 px-8 py-4 rounded-full font-semibold text-base" data-testid="devineur-start-btn">
                    <Wand2 size={20} /> {c.start}
                  </button>
                </motion.div>
              )}

              {phase === "thinking" && (
                <motion.div key="thinking" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="flex flex-col items-center py-6" data-testid="devineur-thinking">
                  <Loader2 size={30} className="animate-spin text-primary mb-4" />
                  <p className="text-primary/70 text-sm uppercase tracking-[0.25em] animate-pulse">{c.thinking}</p>
                </motion.div>
              )}

              {phase === "asking" && current && (
                <motion.div key="asking" initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="max-w-xl mx-auto" data-testid="devineur-question-block">
                  <p className="text-primary/60 text-xs uppercase tracking-[0.25em] mb-3">{c.questionLabel} {answered + 1}</p>
                  <p className="font-display text-2xl sm:text-3xl text-foreground/90 leading-snug mb-8" data-testid="devineur-question">{current.content}</p>
                  <div className="flex flex-wrap items-center justify-center gap-3">
                    <button onClick={() => answer(c.yes)} className="btn-frost-orange inline-flex items-center gap-2 px-7 py-3 rounded-full font-semibold text-sm" data-testid="devineur-answer-yes">
                      <Check size={16} /> {c.yes}
                    </button>
                    <button onClick={() => answer(c.no)} className="inline-flex items-center gap-2 px-7 py-3 rounded-full border border-primary/40 text-primary hover:bg-primary/10 transition-colors font-semibold text-sm" data-testid="devineur-answer-no">
                      <X size={16} /> {c.no}
                    </button>
                    <button onClick={() => answer(c.maybe)} className="inline-flex items-center gap-2 px-5 py-3 rounded-full border border-white/15 text-foreground/70 hover:text-primary hover:border-primary/40 transition-colors font-semibold text-sm" data-testid="devineur-answer-maybe">
                      <CircleHelp size={16} /> {c.maybe}
                    </button>
                    <button onClick={() => answer(c.unknown)} className="inline-flex items-center gap-2 px-5 py-3 rounded-full border border-white/15 text-foreground/60 hover:text-foreground transition-colors font-semibold text-sm" data-testid="devineur-answer-unknown">
                      <HelpCircle size={16} /> {c.unknown}
                    </button>
                  </div>
                </motion.div>
              )}

              {phase === "guessing" && current && (
                <motion.div key="guessing" initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }} className="max-w-xl mx-auto" data-testid="devineur-guess-block">
                  <p className="text-primary/60 text-xs uppercase tracking-[0.25em] mb-3">{c.guessLead}</p>
                  <p className="font-display text-3xl sm:text-4xl text-gradient-orange leading-tight mb-8 pb-[0.06em]" data-testid="devineur-guess">{current.content}</p>
                  <div className="flex flex-wrap items-center justify-center gap-3">
                    <button onClick={guessYes} className="btn-frost-orange inline-flex items-center gap-2 px-7 py-3 rounded-full font-semibold text-sm" data-testid="devineur-guess-yes">
                      <Sparkles size={16} /> {c.guessYes}
                    </button>
                    <button onClick={guessNo} className="inline-flex items-center gap-2 px-7 py-3 rounded-full border border-primary/40 text-primary hover:bg-primary/10 transition-colors font-semibold text-sm" data-testid="devineur-guess-no">
                      <X size={16} /> {c.guessNo}
                    </button>
                  </div>
                </motion.div>
              )}

              {phase === "won" && (
                <motion.div key="won" initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="flex flex-col items-center" data-testid="devineur-won">
                  <span className="w-16 h-16 rounded-full bg-primary/15 text-primary flex items-center justify-center mb-4"><Sparkles size={30} /></span>
                  <p className="font-display text-gradient-orange text-3xl sm:text-4xl mb-1 pb-[0.06em]">{c.wonTitle}</p>
                  {wonAnswer && <p className="font-display italic text-xl sm:text-2xl text-foreground/85 mb-2" data-testid="devineur-won-answer">« {wonAnswer} »</p>}
                  <p className="text-foreground/65 mb-3" data-testid="devineur-won-count">{c.wonSub(wonQuestions)}</p>
                  {isRecord && (
                    <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-primary/15 border border-primary/40 text-primary text-xs font-semibold uppercase tracking-wider mb-6" data-testid="devineur-record-badge">
                      <Trophy size={14} /> {c.recordBadge}
                    </span>
                  )}

                  <div className="w-full max-w-sm mb-7 mt-2" data-testid="devineur-leaderboard">
                    <p className="text-primary/60 text-[0.7rem] uppercase tracking-[0.25em] mb-2.5 flex items-center justify-center gap-2"><Trophy size={13} /> {c.globalTitle}</p>
                    <div className="flex items-center justify-center gap-1.5 mb-3" data-testid="devineur-period-toggle">
                      <button onClick={() => switchPeriod("week")} data-testid="devineur-period-week"
                        className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${period === "week" ? "border-primary/50 bg-primary/12 text-primary" : "border-white/10 text-foreground/55 hover:text-primary hover:border-primary/30"}`}>
                        {c.weekTab}
                      </button>
                      <button onClick={() => switchPeriod("all")} data-testid="devineur-period-all"
                        className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${period === "all" ? "border-primary/50 bg-primary/12 text-primary" : "border-white/10 text-foreground/55 hover:text-primary hover:border-primary/30"}`}>
                        {c.allTab}
                      </button>
                    </div>
                    {!submitted ? (
                      <div className="flex items-center gap-2 mb-3">
                        <input value={name} onChange={(e) => setName(e.target.value)} maxLength={24} placeholder={c.pseudoPlaceholder} data-testid="devineur-name-input"
                          className="flex-1 min-w-0 bg-black/30 border border-white/12 rounded-full px-4 py-2.5 text-sm text-foreground/90 outline-none focus:border-primary/40" />
                        <button onClick={publishScore} disabled={publishing || !name.trim()} data-testid="devineur-publish-btn"
                          className="btn-frost-orange inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full font-semibold text-xs disabled:opacity-50 whitespace-nowrap flex-shrink-0">
                          {publishing ? <Loader2 size={14} className="animate-spin" /> : <Trophy size={14} />} {c.publish}
                        </button>
                      </div>
                    ) : (
                      <p className="text-primary/90 text-sm mb-3" data-testid="devineur-published">{c.publishedRank(myRank)}</p>
                    )}
                    {board.length > 0 ? (
                      <div className="space-y-1.5" data-testid="devineur-global-board">
                        {board.map((g, i) => (
                          <div key={i} className={`flex items-center gap-3 rounded-xl border px-4 py-2.5 ${i === 0 ? "border-primary/40 bg-primary/[0.06]" : "border-white/8 bg-white/[0.03]"}`} data-testid={`devineur-board-item-${i}`}>
                            <span className={`w-5 flex-shrink-0 flex items-center justify-center ${i < 3 ? "text-primary" : "text-foreground/35"}`}>
                              {i === 0 ? <Trophy size={14} /> : <span className="text-xs font-mono">{i + 1}</span>}
                            </span>
                            <span className="text-sm text-foreground/85 truncate flex-shrink-0 max-w-[6rem] text-left">{g.name}</span>
                            <span className="text-xs text-foreground/45 truncate flex-1 text-left italic">« {g.answer} »</span>
                            <span className={`text-xs flex-shrink-0 ${i === 0 ? "text-primary" : "text-foreground/50"}`}>{g.questions} {c.questionsShort}</span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-foreground/40 text-sm italic" data-testid="devineur-global-empty">{c.globalEmpty}</p>
                    )}
                  </div>

                  <div className="flex flex-wrap items-center justify-center gap-3">
                    <button onClick={shareCard} disabled={sharing} className="btn-frost-orange inline-flex items-center gap-2 px-6 py-3.5 rounded-full font-semibold disabled:opacity-60" data-testid="devineur-share-btn">
                      {sharing ? <Loader2 size={17} className="animate-spin" /> : <Share2 size={17} />} {c.shareBtn}
                    </button>
                    <button onClick={start} className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full border border-primary/30 text-primary hover:bg-primary/10 transition-colors font-semibold" data-testid="devineur-replay-btn">
                      <RotateCcw size={17} /> {c.replay}
                    </button>
                  </div>
                </motion.div>
              )}

              {phase === "give" && (
                <motion.div key="give" initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="flex flex-col items-center" data-testid="devineur-give">
                  <span className="w-16 h-16 rounded-full bg-white/8 text-foreground/60 flex items-center justify-center mb-4"><HelpCircle size={30} /></span>
                  <p className="font-display text-3xl text-foreground/85 mb-2">{c.giveTitle}</p>
                  <p className="text-foreground/60 max-w-md mb-7 leading-relaxed">{c.giveSub}</p>
                  <button onClick={start} className="btn-frost-orange inline-flex items-center gap-2 px-7 py-3.5 rounded-full font-semibold" data-testid="devineur-give-replay-btn">
                    <RotateCcw size={17} /> {c.replay}
                  </button>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>
  );
};
